/**
 * MissionPanel — painel "Mission Control" do visual Sphere (cartão do Dashboard e
 * topo da tela Mission Control).
 *
 * Composição da imagem aprovada: resumo à esquerda (agentes ativos, projetos em
 * andamento, aguardando aprovação), núcleo orbital no centro (esfera WebGL com
 * anéis e cubos em órbita; "Agentes em execução N" em HTML) e as 4 macroetapas à
 * direita (ícone, estado, detalhe). Todo número vem do domínio; ausente = "—".
 * O estado de conexão é separado do estado de domínio: conexão perdida mantém o
 * último snapshot, avisa e PARA a animação (nunca aparenta processamento).
 */
import { useId } from 'react';
import {
  Users, Activity, Shield, Box, CodeXml, CircleCheck, Rocket, CircleAlert, ArrowRight, WifiOff,
} from 'lucide-react';
import { inteiro, dataHora } from '@/lib/format';
import { MACROETAPAS, detalhePadrao } from './missionModel';
import { MissionOrbit } from './SphereCanvas';

const ICONE = { planning: Box, development: CodeXml, validation: CircleCheck, deploy: Rocket };

const ROTULO_DO_ESTADO = {
  idle: 'Nenhuma execução em andamento',
  running: 'Em execução',
  approval: 'Aguardando aprovação',
  paused: 'Pausado',
  completed: 'Concluída',
  error: 'Falha na execução',
};

function rotuloDoStatusDaEtapa(status) {
  return detalhePadrao(status);
}

function StageList({ stages }) {
  return (
    <ol className="sp-stages" aria-label="Macroetapas da execução" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {stages.map((st) => {
        const meta = MACROETAPAS.find((m) => m.id === st.id);
        const Icon = st.status === 'error' ? CircleAlert : st.status === 'approval' ? Shield : ICONE[st.id];
        const detalhe = st.detail || rotuloDoStatusDaEtapa(st.status);
        return (
          <li key={st.id} className="sp-stage" data-status={st.status} data-stage={st.id}>
            <span className="sp-stage-icon" aria-hidden="true"><Icon size={18} strokeWidth={1.75} /></span>
            <span className="sp-stage-copy">
              <strong>{meta?.label}</strong>
              <small title={detalhe}>
                <span className="sp-sr-only">{rotuloDoStatusDaEtapa(st.status)}: </span>{detalhe}
              </small>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * @param {object} p
 * @param {object|null} p.snapshot   derivarMissao(...) (ou null sem execução)
 * @param {{activeAgents:number|null, activeProjects:number|null, pendingGates:number|null}} p.counts
 * @param {'ok'|'carregando'|'perdida'|'erro'} [p.conexao]
 * @param {string|null} [p.acompanhando]  nome do projeto da execução exibida
 * @param {string} [p.actionLabel] / p.onAction  botão do cabeçalho
 * @param {() => void} [p.onGates]   abre a fila de aprovação
 * @param {() => void} [p.onRetry]
 * @param {string} [p.title] / p.subtitle / p.eyebrow
 */
export function MissionPanel({
  snapshot, counts, conexao = 'ok', acompanhando = null, actionLabel, onAction, onGates, onRetry,
  eyebrow = 'Execução em tempo real', title = 'Mission Control',
  subtitle = 'Acompanhe os agentes em ação e o progresso dos seus projetos.', headingLevel = 2,
}) {
  const uid = useId();
  const titleId = `sp-mission-title-${uid.replace(/:/g, '')}`;
  const state = snapshot?.state ?? 'idle';
  const stages = snapshot?.stages ?? MACROETAPAS.map((m) => ({ id: m.id, status: 'pending', detail: detalhePadrao('pending') }));
  const agentes = counts?.activeAgents ?? null;
  const gates = counts?.pendingGates ?? null;
  const perdida = conexao === 'perdida';
  // Conexão perdida: mostra o último estado, mas sem animação de processamento.
  const estadoDaCena = perdida && state === 'running' ? 'paused' : state;
  const H = headingLevel === 1 ? 'h1' : 'h2';

  return (
    <section className="sp-panel sp-mission" data-state={state} data-conexao={conexao} aria-labelledby={titleId}>
      <div className="sp-mission-layout">
        <div className="sp-mission-left">
          <div className="sp-mission-head">
            <p className="sp-eyebrow">{eyebrow}</p>
            <H id={titleId}>{title}</H>
            {subtitle && <p className="sp-mission-subtitle">{subtitle}</p>}
          </div>
          <div className="sp-mission-counts">
            <div className="sp-count">
              <Users size={16} strokeWidth={1.75} aria-hidden="true" />
              <strong className="sp-num">{inteiro(agentes)}</strong> Agentes ativos
            </div>
            <div className="sp-count" data-tone="blue">
              <Activity size={16} strokeWidth={1.75} aria-hidden="true" />
              <strong className="sp-num">{inteiro(counts?.activeProjects ?? null)}</strong> Projetos em andamento
            </div>
            {onGates && Number(gates) > 0 ? (
              <button type="button" className="sp-count" data-tone="amber" onClick={onGates}
                style={{ cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                aria-label={`${inteiro(gates)} aguardando aprovação — abrir a fila de gates`}>
                <Shield size={16} strokeWidth={1.75} aria-hidden="true" />
                <strong className="sp-num">{inteiro(gates)}</strong> Aguardando aprovação
              </button>
            ) : (
              <div className="sp-count" data-tone="amber">
                <Shield size={16} strokeWidth={1.75} aria-hidden="true" />
                <strong className="sp-num">{inteiro(gates)}</strong> Aguardando aprovação
              </div>
            )}
          </div>
        </div>
        {onAction && (
          <div className="sp-mission-action">
            <button type="button" className="sp-btn sp-btn--ghost" onClick={onAction}>
              {actionLabel} <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        )}

        <MissionOrbit state={estadoDaCena} value={inteiro(agentes)} />

        <StageList stages={stages} />
      </div>

      <div className="sp-mission-foot">
        <span>
          {ROTULO_DO_ESTADO[state]}
          {acompanhando ? <> · <span title={acompanhando}>{acompanhando}</span></> : null}
          {snapshot && state !== 'idle' ? <> · atualizado {dataHora(snapshot.updatedAt)}</> : null}
        </span>
        {perdida && (
          <span className="sp-conn" data-conn="lost" role="status">
            <WifiOff size={14} strokeWidth={1.75} aria-hidden="true" /> Conexão interrompida — mostrando o último estado
            {onRetry && (
              <button type="button" className="sp-btn" style={{ minHeight: 28, padding: '0 10px', marginLeft: 6 }} onClick={onRetry}>
                Tentar novamente
              </button>
            )}
          </span>
        )}
        {conexao === 'erro' && (
          <span className="sp-conn" data-conn="lost" role="alert">
            <WifiOff size={14} strokeWidth={1.75} aria-hidden="true" /> Não foi possível carregar a execução
            {onRetry && (
              <button type="button" className="sp-btn" style={{ minHeight: 28, padding: '0 10px', marginLeft: 6 }} onClick={onRetry}>
                Tentar novamente
              </button>
            )}
          </span>
        )}
      </div>

      <p className="sp-sr-only" role="status" aria-live="polite">
        {ROTULO_DO_ESTADO[state]}. {inteiro(agentes)} agentes ativos; {inteiro(gates)} aprovações pendentes.
      </p>
    </section>
  );
}
